import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { processReportUpload, validateReportFile, confirmAndSaveHormones } from "../lib/reportUpload";

// FEATURE 2: Hormone Pattern Detection Utility
function detectHormonePattern(h) {
  const flags = [];
  if (parseFloat(h.lhFshRatio) > 2) flags.push('elevated_lh_fsh');
  if (parseFloat(h.testosterone) > 70) flags.push('elevated_testosterone');
  if (parseFloat(h.insulin) > 12) flags.push('elevated_insulin');
  if (parseFloat(h.amh) > 3.5) flags.push('elevated_amh');

  if (flags.includes('elevated_lh_fsh') && flags.includes('elevated_testosterone'))
    return { pattern: 'Classic PCOS Hormonal Pattern', severity: 'high', flags };
  if (flags.includes('elevated_insulin') && flags.length >= 2)
    return { pattern: 'Insulin-Resistant Pattern', severity: 'moderate', flags };
  if (flags.length === 1)
    return { pattern: 'Mild Hormonal Variation', severity: 'low', flags };
  if (flags.length === 0)
    return { pattern: 'Within Common Reference Ranges', severity: 'none', flags };
  return { pattern: 'Mixed Hormonal Pattern', severity: 'moderate', flags };
}

// ENHANCED: Generate overall hormonal assessment
function getOverallHormoneAssessment(hormoneData) {
  if (!hormoneData) return null;
  
  const lh = parseFloat(hormoneData.lh) || 0;
  const fsh = parseFloat(hormoneData.fsh) || 0;
  const testosterone = parseFloat(hormoneData.testosterone) || 0;
  const insulin = parseFloat(hormoneData.insulin) || 0;
  const lhFshRatio = fsh > 0 ? (lh / fsh) : 0;
  
  // Calculate individual statuses
  const statuses = {
    lh: lh < 2 ? 'low' : lh <= 10 ? 'normal' : 'elevated',
    fsh: fsh < 3 ? 'low' : fsh <= 10 ? 'normal' : 'elevated',
    testosterone: testosterone < 15 ? 'low' : testosterone <= 70 ? 'normal' : 'elevated',
    insulin: insulin <= 8 ? 'normal' : insulin <= 12 ? 'borderline' : 'elevated',
    lhFshRatio: lhFshRatio < 1.5 ? 'normal' : lhFshRatio <= 2 ? 'borderline' : 'elevated'
  };
  
  // Count abnormalities
  const elevatedCount = Object.values(statuses).filter(s => s === 'elevated').length;
  const borderlineCount = Object.values(statuses).filter(s => s === 'borderline').length;
  
  // Determine overall assessment
  let overall, interpretation, color;
  
  if (elevatedCount >= 2 || (statuses.lhFshRatio === 'elevated' && statuses.testosterone === 'elevated')) {
    overall = 'PCOS Pattern';
    interpretation = 'Multiple markers suggest a hormonal pattern commonly associated with PCOS.';
    color = { bg: '#FEE2E2', text: '#991B1B', badge: '#DC2626' };
  } else if (elevatedCount === 1 || borderlineCount >= 2) {
    overall = 'Imbalanced';
    interpretation = 'Some hormone levels are outside typical ranges. Worth monitoring.';
    color = { bg: '#FEF3C7', text: '#92400E', badge: '#D97706' };
  } else if (borderlineCount === 1) {
    overall = 'Borderline';
    interpretation = 'Hormone levels are mostly normal with minor variations.';
    color = { bg: '#FEF9C3', text: '#854D0E', badge: '#CA8A04' };
  } else {
    overall = 'Normal';
    interpretation = 'All key hormones are within healthy reference ranges.';
    color = { bg: '#D1FAE5', text: '#065F46', badge: '#059669' };
  }
  
  return {
    overall,
    interpretation,
    color,
    statuses,
    values: { lh, fsh, testosterone, insulin, lhFshRatio: lhFshRatio.toFixed(2) }
  };
}

// FEATURE 1: LH/FSH Ratio Status
function getRatioStatus(ratio) {
  const r = parseFloat(ratio);
  if (r < 1.5) return { label: "Common Pattern", bg: "#D1FAE5", color: "#065F46" };
  if (r <= 2.0) return { label: "Mild Elevation", bg: "#FEF9C3", color: "#854D0E" };
  return { label: "May Indicate PCOS Pattern", bg: "#FDE8E8", color: "#9B1C1C" };
}

const hormoneConfig = [
  { key: "lh", label: "LH", unit: "mIU/mL", placeholder: "e.g. 7.2", upperRef: 20, refRange: "2–10", refRangeNote: "follicular phase", lowRef: 2, highRef: 10, maxScale: 20 },
  { key: "fsh", label: "FSH", unit: "mIU/mL", placeholder: "e.g. 5.1", upperRef: 15, refRange: "3–10", refRangeNote: "follicular phase", lowRef: 3, highRef: 10, maxScale: 15 },
  { key: "insulin", label: "Fasting Insulin", unit: "µIU/mL", placeholder: "e.g. 10.4", upperRef: 25, refRange: "2–12", refRangeNote: "fasting", lowRef: 2, highRef: 12, maxScale: 25 },
  { key: "testosterone", label: "Total Testosterone", unit: "ng/dL", placeholder: "e.g. 45.0", upperRef: 100, refRange: "15–70", refRangeNote: "females", lowRef: 15, highRef: 70, maxScale: 100 },
  { key: "amh", label: "AMH", unit: "ng/mL", placeholder: "e.g. 3.8", upperRef: 8, refRange: "1–3.5", refRangeNote: "reproductive age", lowRef: 1, highRef: 3.5, maxScale: 8 },
];

const getStatus = (key, value) => {
  const v = parseFloat(value);
  if (isNaN(v)) return { label: "N/A", type: "normal" };
  switch (key) {
    case "lh": return v < 2 ? { label: "Low", type: "low" } : v <= 10 ? { label: "Normal", type: "normal" } : { label: "Elevated", type: "elevated" };
    case "fsh": return v < 3 ? { label: "Low", type: "low" } : v <= 10 ? { label: "Normal", type: "normal" } : { label: "Elevated", type: "elevated" };
    case "insulin": return v < 8 ? { label: "Normal", type: "normal" } : v <= 12 ? { label: "Borderline", type: "borderline" } : { label: "Elevated", type: "elevated" };
    case "testosterone": return v < 15 ? { label: "Low", type: "low" } : v <= 70 ? { label: "Normal", type: "normal" } : { label: "Elevated", type: "elevated" };
    case "amh": return v < 1 ? { label: "Low", type: "low" } : v <= 3.5 ? { label: "Normal", type: "normal" } : { label: "Elevated", type: "elevated" };
    default: return { label: "Normal", type: "normal" };
  }
};

const interpretations = {
  lh: { 
    low: {
      meaning: "Your LH (Luteinizing Hormone) level is below the typical range.",
      pcos: "Low LH is less common in PCOS. It may indicate hypothalamic or pituitary issues affecting your menstrual cycle and ovulation.",
      lifestyle: "Focus on stress management, adequate sleep, and maintaining a healthy weight. Consider discussing with an endocrinologist."
    },
    normal: {
      meaning: "Your LH level falls within the normal reference range, indicating your pituitary gland is functioning well.",
      pcos: "Normal LH is a positive sign, though the LH/FSH ratio is also important in PCOS assessment. Your body's signaling for ovulation appears healthy.",
      lifestyle: "Continue maintaining balanced nutrition, regular exercise, and good sleep habits to support hormonal health."
    },
    elevated: {
      meaning: "Your LH level is higher than the typical range. LH triggers ovulation when it surges mid-cycle.",
      pcos: "Elevated LH is one of the hallmark findings in PCOS. A high LH/FSH ratio (above 2:1) can disrupt normal ovulation and contribute to irregular cycles.",
      lifestyle: "Anti-inflammatory foods, regular physical activity, and stress reduction may help. Inositol supplements have shown promise in some studies."
    }
  },
  fsh: { 
    low: {
      meaning: "Your FSH (Follicle Stimulating Hormone) level is below the typical range.",
      pcos: "Low FSH can affect follicle development in your ovaries. In PCOS, FSH is sometimes suppressed while LH remains elevated.",
      lifestyle: "Ensure adequate nutrition, especially healthy fats and proteins. Avoid extreme dieting which can suppress FSH production."
    },
    normal: {
      meaning: "Your FSH level is within the normal range, suggesting good communication between your brain and ovaries.",
      pcos: "Normal FSH is encouraging. Combined with your LH level, it helps paint a picture of your hormonal balance and ovarian function.",
      lifestyle: "Maintain a balanced diet rich in antioxidants, stay active, and manage stress to continue supporting healthy hormone levels."
    },
    elevated: {
      meaning: "Your FSH level is higher than typical, which may indicate your ovaries need more stimulation.",
      pcos: "Elevated FSH is less common in PCOS and may suggest diminished ovarian reserve or perimenopause. Further evaluation is recommended.",
      lifestyle: "Focus on overall health with nutrient-dense foods, CoQ10-rich foods, and avoiding smoking which can affect ovarian function."
    }
  },
  insulin: { 
    normal: {
      meaning: "Your fasting insulin level indicates healthy insulin sensitivity. Your body is efficiently using insulin to regulate blood sugar.",
      pcos: "Good insulin sensitivity is protective against many PCOS complications. It helps maintain regular cycles and reduces androgen production.",
      lifestyle: "Continue eating balanced meals with fiber and protein, staying active, and avoiding prolonged sitting to maintain this healthy level."
    },
    borderline: {
      meaning: "Your insulin level is in the borderline range, suggesting your body may be working harder to control blood sugar.",
      pcos: "Borderline insulin can be an early sign of insulin resistance, which affects up to 70% of women with PCOS and drives many symptoms.",
      lifestyle: "Consider reducing refined carbs, increasing fiber intake, adding resistance training, and eating protein with each meal."
    },
    elevated: {
      meaning: "Your fasting insulin is elevated, indicating insulin resistance. Your pancreas is producing extra insulin to manage blood sugar.",
      pcos: "Insulin resistance is a key driver of PCOS symptoms. High insulin stimulates the ovaries to produce more androgens, worsening symptoms like acne and hair growth.",
      lifestyle: "Prioritize low-glycemic foods, regular exercise (especially strength training), adequate sleep, and stress management."
    }
  },
  testosterone: { 
    low: {
      meaning: "Your total testosterone level is below the typical female range.",
      pcos: "Low testosterone is unusual in PCOS. It may indicate other hormonal conditions or could result from certain medications.",
      lifestyle: "Ensure adequate protein intake, healthy fats, and strength training. Discuss with your healthcare provider if you experience fatigue or low libido."
    },
    normal: {
      meaning: "Your testosterone level is within the normal range for females, suggesting balanced androgen production.",
      pcos: "Normal testosterone is a positive indicator. Even with PCOS, keeping androgens in check helps minimize symptoms like acne and excess hair growth.",
      lifestyle: "Support hormonal balance with anti-inflammatory foods, spearmint tea (shown to have anti-androgen effects), and regular physical activity."
    },
    elevated: {
      meaning: "Your testosterone level is higher than the typical female range, indicating increased androgen production.",
      pcos: "Elevated testosterone is common in PCOS and contributes to symptoms like acne, oily skin, hair thinning on the scalp, and excess facial/body hair.",
      lifestyle: "Focus on insulin management (as insulin drives androgen production), anti-inflammatory diet, spearmint tea, and stress reduction."
    }
  },
  amh: { 
    low: {
      meaning: "Your AMH (Anti-Müllerian Hormone) level is below typical, reflecting fewer remaining egg-containing follicles.",
      pcos: "Low AMH is uncommon in PCOS. It may indicate diminished ovarian reserve, which could affect fertility planning timelines.",
      lifestyle: "Avoid smoking, limit alcohol, maintain a healthy weight, and consider antioxidant-rich foods. If fertility is a concern, consult a reproductive specialist."
    },
    normal: {
      meaning: "Your AMH level is within the normal range, suggesting a healthy number of remaining ovarian follicles.",
      pcos: "Normal AMH indicates good ovarian reserve. In PCOS, AMH is often elevated, so a normal level is reassuring.",
      lifestyle: "Continue supporting overall health with balanced nutrition, regular exercise, and avoiding environmental toxins when possible."
    },
    elevated: {
      meaning: "Your AMH level is higher than typical, suggesting an increased number of small follicles in your ovaries.",
      pcos: "Elevated AMH is very common in PCOS due to the many small follicles (appearing as 'cysts' on ultrasound). This reflects the characteristic polycystic ovarian morphology.",
      lifestyle: "Focus on lifestyle factors that support overall hormonal balance: regular exercise, stress management, and anti-inflammatory nutrition."
    }
  },
};

const buildAnalysis = (raw) => {
  let flagCount = 0;
  const analyzed = {};
  // Map DB column name (testosteone) to UI key (testosterone)
  const normalizedRaw = { ...raw };
  if (raw.testosteone !== undefined) {
    normalizedRaw.testosterone = raw.testosteone;
  }
  hormoneConfig.forEach((h) => {
    const status = getStatus(h.key, normalizedRaw[h.key]);
    analyzed[h.key] = {
      value: parseFloat(normalizedRaw[h.key]),
      status,
      interpretation: interpretations[h.key]?.[status.type] || "Within commonly cited normal range.",
      barWidth: Math.min((parseFloat(normalizedRaw[h.key]) / h.upperRef) * 100, 100),
    };
    if (status.type !== "normal") flagCount++;
  });
  analyzed.flagCount = flagCount;
  return analyzed;
};

export default function HormonesScreen({ userId }) {
  const [subTab, setSubTab] = useState("upload");
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadState, setUploadState] = useState("idle");
  const [formData, setFormData] = useState({ lh: "", fsh: "", insulin: "", testosterone: "", amh: "" });
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [hormonePattern, setHormonePattern] = useState(null);
  const [hormoneHistory, setHormoneHistory] = useState([]);
  const [hormoneAssessment, setHormoneAssessment] = useState(null);
  const [uploadError, setUploadError] = useState("");
  // Review screen state: appears after OCR extraction, before saving to hormone_reports
  const [reviewValues, setReviewValues] = useState(null); // { lh, fsh, insulin, testosterone, amh, summary, raw_text }
  const fileInputRef = useRef(null);
  const didFetch = useRef(false);

  // FEATURE 1: Calculate LH/FSH Ratio
  const lhFshRatio = (formData.lh && formData.fsh && parseFloat(formData.lh) > 0 && parseFloat(formData.fsh) > 0)
    ? (parseFloat(formData.lh) / parseFloat(formData.fsh)).toFixed(2)
    : null;

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    const fetchLatest = async () => {
      try {
        console.log('[HormonesScreen] Fetching hormone data for user:', userId);
        const { data, error } = await supabase
          .from("hormone_reports")
          .select("*")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (error) {
          console.error("[HormonesScreen] Fetch error:", error.message);
          return;
        }
        
        if (data) {
          console.log("[HormonesScreen] Fetched hormone data:", data);
          setResults(buildAnalysis(data));
          setShowResults(true);
          setTimeout(() => setMounted(true), 50);
          
          // Rebuild pattern and assessment from DB data
          const hormoneData = {
            lh: String(data.lh || ''),
            fsh: String(data.fsh || ''),
            insulin: String(data.insulin || ''),
            testosterone: String(data.testosterone || data.testosteone || ''),
            amh: String(data.amh || ''),
            lhFshRatio: data.lh && data.fsh ? (data.lh / data.fsh).toFixed(2) : null
          };
          
          const patternResult = detectHormonePattern(hormoneData);
          setHormonePattern(patternResult);
          
          const assessment = getOverallHormoneAssessment(hormoneData);
          setHormoneAssessment(assessment);
          console.log("[HormonesScreen] Assessment:", assessment?.status, "| Pattern:", patternResult.pattern);
        } else {
          console.log("[HormonesScreen] No hormone data found");
        }
      } catch (err) {
        console.error("[HormonesScreen] Exception:", err.message);
      }
    };
    fetchLatest();
    
    // Load hormone history from Supabase
    const fetchHistory = async () => {
      const { data: historyRows } = await supabase
        .from("hormone_reports")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(10);
      
      if (historyRows && historyRows.length > 0) {
        const history = historyRows.map(r => ({
          date: r.created_at,
          lh: r.lh, fsh: r.fsh, insulin: r.insulin,
          testosterone: r.testosterone || r.testosteone, amh: r.amh,
          lhFshRatio: r.lh && r.fsh ? (r.lh / r.fsh).toFixed(2) : null,
          pattern: detectHormonePattern({
            lh: String(r.lh || ''), fsh: String(r.fsh || ''),
            insulin: String(r.insulin || ''), testosterone: String(r.testosterone || r.testosteone || ''),
            amh: String(r.amh || ''),
            lhFshRatio: r.lh && r.fsh ? (r.lh / r.fsh).toFixed(2) : null
          }).pattern
        }));
        setHormoneHistory(history);
      }
    };
    fetchHistory();
  }, [userId]);

  const handleFileChange = (e) => {
    if (e.target.files[0]) setSelectedFile(e.target.files[0].name);
  };

  const handleAnalyzeUpload = () => {
    setUploadState("reading");
    setTimeout(() => { setUploadState("fallback"); setSubTab("manual"); }, 1800);
  };

  const handleAnalyze = async (e) => {
    if (e) e.preventDefault();
    
    const allFilled = hormoneConfig.every((h) => formData[h.key] && !isNaN(parseFloat(formData[h.key])));
    if (!allFilled) return;

    setSaving(true);
    setFetchError("");

    // Calculate LH/FSH ratio
    const currentRatio = (parseFloat(formData.lh) / parseFloat(formData.fsh)).toFixed(2);
    const hormoneData = { ...formData, lhFshRatio: currentRatio };

    // Detect pattern
    const patternResult = detectHormonePattern(hormoneData);
    setHormonePattern(patternResult);
    
    // Calculate assessment
    const assessment = getOverallHormoneAssessment(hormoneData);
    setHormoneAssessment(assessment);

    // Build and show analysis results immediately
    const analyzed = buildAnalysis(formData);
    setResults(analyzed);
    setShowResults(true);
    setMounted(false);
    setTimeout(() => setMounted(true), 50);

    // ── Save to Supabase (source of truth) ──
    const row = {
      user_id: userId,
      lh: parseFloat(formData.lh),
      fsh: parseFloat(formData.fsh),
      insulin: parseFloat(formData.insulin),
      testosterone: parseFloat(formData.testosterone),
      amh: parseFloat(formData.amh),
      report_date: new Date().toISOString().split("T")[0],
    };

    // ── Save to Supabase using XHR to bypass fetch interceptor ──
    console.log("Saving hormone data:", userId, row);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      const xhrResult = await new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${supabaseUrl}/rest/v1/hormone_reports`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('apikey', supabaseKey);
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.setRequestHeader('Prefer', 'return=representation');
        xhr.onload = () => {
          let parsed = null;
          try { parsed = JSON.parse(xhr.responseText); } catch (_e) { /* ignore parse errors */ }
          resolve({ status: xhr.status, data: parsed, text: xhr.responseText });
        };
        xhr.onerror = () => resolve({ status: 0, data: null, text: 'network error' });
        xhr.send(JSON.stringify(row));
      });
      
      if (xhrResult.status >= 200 && xhrResult.status < 300) {
        console.log("[HormonesScreen] Hormone data saved to Supabase ✓");
        console.log("Fetched hormone data:", xhrResult.data);
        
        // Update history from DB
        const { data: historyRows } = await supabase
          .from("hormone_reports")
          .select("*")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(10);
        
        if (historyRows) {
          setHormoneHistory(historyRows.map(r => ({
            date: r.created_at,
            lh: r.lh, fsh: r.fsh, insulin: r.insulin,
            testosterone: r.testosterone || r.testosteone, amh: r.amh,
            lhFshRatio: r.lh && r.fsh ? (r.lh / r.fsh).toFixed(2) : null
          })));
        }
      } else {
        console.error("[HormonesScreen] Save failed:", xhrResult.status, xhrResult.text?.substring(0, 200));
      }
    } catch (err) {
      console.error("[HormonesScreen] Save exception:", err.message);
    }

    setSaving(false);
  };

  const handleReenter = () => {
    setShowResults(false);
    setUploadState("idle");
    setSelectedFile(null);
    setImagePreview(null);
    setMounted(false);
  };

  // Handle file selection with image preview
  const handleFileSelect = (file) => {
    setUploadError("");
    if (!file) return;
    const err = validateReportFile(file);
    if (err) {
      setUploadError(err);
      return;
    }
    setSelectedFile(file);
    // Create preview for images only
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      // PDF — show a placeholder preview
      setImagePreview(null);
    }
  };

  // REAL upload + OCR pipeline. After extraction, show Review screen for user confirmation.
  const handleUploadAndEdit = async () => {
    if (!selectedFile) return;
    setUploadError("");
    setUploadState("uploading");

    try {
      const result = await processReportUpload(userId, selectedFile, (phase) => {
        if (phase === "extracting") setUploadState("extracting");
        else if (phase === "analyzing") setUploadState("analyzing");
        else setUploadState("uploading");
      });

      if (result.error) {
        // File uploaded but extraction failed — fall back to manual entry
        setUploadError(result.error + " You can still enter values manually below.");
        setUploadState("fallback");
        setSubTab("manual");
        return;
      }

      const extracted = result.extraction?.extracted || {};
      const status = result.extraction?.status;
      const summary = result.extraction?.summary || "";

      if (status !== "completed") {
        setUploadError("Extraction failed. Please try again or enter values manually.");
        setUploadState("fallback");
        setSubTab("manual");
        return;
      }

      const hasValues = ["lh", "fsh", "insulin", "testosterone", "amh"].some(
        (k) => extracted[k] !== null && extracted[k] !== undefined
      );

      if (!hasValues) {
        setUploadError("No hormone values could be detected. Please enter them manually.");
        setUploadState("fallback");
        setSubTab("manual");
        return;
      }

      // Show REVIEW screen — user must confirm or edit before saving
      setReviewValues({
        lh: extracted.lh !== null && extracted.lh !== undefined ? String(extracted.lh) : "",
        fsh: extracted.fsh !== null && extracted.fsh !== undefined ? String(extracted.fsh) : "",
        insulin: extracted.insulin !== null && extracted.insulin !== undefined ? String(extracted.insulin) : "",
        testosterone: extracted.testosterone !== null && extracted.testosterone !== undefined ? String(extracted.testosterone) : "",
        amh: extracted.amh !== null && extracted.amh !== undefined ? String(extracted.amh) : "",
        summary,
      });
      setUploadState("review");
    } catch (err) {
      console.error("[HormonesScreen] Upload pipeline failed:", err);
      setUploadError(err.message || "Upload failed. Please try again.");
      setUploadState("idle");
    }
  };

  // Update a single review-screen field (used by Edit Values mode)
  const updateReviewField = (key, val) => {
    setReviewValues((prev) => ({ ...prev, [key]: val }));
  };

  // User confirms reviewed values → save to hormone_reports + run analysis engine
  const handleConfirmReview = async () => {
    if (!reviewValues) return;
    setUploadState("analyzing");

    // Build numeric payload — only include keys with valid numbers
    const numeric = {};
    ["lh", "fsh", "insulin", "testosterone", "amh"].forEach((k) => {
      const v = reviewValues[k];
      if (v !== "" && v !== null && v !== undefined && !isNaN(parseFloat(v))) {
        numeric[k] = parseFloat(v);
      } else {
        numeric[k] = null;
      }
    });

    // Save to hormone_reports (drives existing analysis engine + Care personalization)
    try {
      await confirmAndSaveHormones(userId, numeric);
    } catch (err) {
      console.error("[HormonesScreen] Save to hormone_reports failed:", err);
      // Continue — even if DB save fails, show the local analysis
    }

    // Run existing analysis engine on the confirmed values
    const formValues = {
      lh: numeric.lh != null ? String(numeric.lh) : "",
      fsh: numeric.fsh != null ? String(numeric.fsh) : "",
      insulin: numeric.insulin != null ? String(numeric.insulin) : "",
      testosterone: numeric.testosterone != null ? String(numeric.testosterone) : "",
      amh: numeric.amh != null ? String(numeric.amh) : "",
    };

    // Only run full analysis if all 5 are present (existing engine requires complete set)
    const allFilled = Object.values(formValues).every((v) => v !== "");
    setFormData(formValues);

    if (allFilled) {
      const ratio = (parseFloat(formValues.lh) / parseFloat(formValues.fsh)).toFixed(2);
      const hormoneData = { ...formValues, lhFshRatio: ratio };
      const patternResult = detectHormonePattern(hormoneData);
      setHormonePattern(patternResult);
      const assessment = getOverallHormoneAssessment(hormoneData);
      setHormoneAssessment(assessment);
      const analyzed = buildAnalysis(formValues);
      setResults(analyzed);
      setShowResults(true);
      setMounted(false);
      setTimeout(() => setMounted(true), 50);
      setReviewValues(null);
      setUploadState("idle");
      setImagePreview(null);
      setSelectedFile(null);

      // Refresh history list
      const { data: historyRows } = await supabase
        .from("hormone_reports")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(10);
      if (historyRows) {
        setHormoneHistory(historyRows.map(r => ({
          date: r.created_at, lh: r.lh, fsh: r.fsh, insulin: r.insulin,
          testosterone: r.testosterone || r.testosteone, amh: r.amh,
          lhFshRatio: r.lh && r.fsh ? (r.lh / r.fsh).toFixed(2) : null,
        })));
      }
    } else {
      // Partial — switch to manual entry pre-filled so user can complete missing values
      setReviewValues(null);
      setUploadState("fallback");
      setSubTab("manual");
    }
  };

  // User wants to edit the extracted values — switch to manual entry with values pre-filled
  const handleEditReview = () => {
    if (!reviewValues) return;
    setFormData({
      lh: reviewValues.lh,
      fsh: reviewValues.fsh,
      insulin: reviewValues.insulin,
      testosterone: reviewValues.testosterone,
      amh: reviewValues.amh,
    });
    setReviewValues(null);
    setUploadState("fallback");
    setSubTab("manual");
  };

  // Discard the review and start over
  const handleCancelReview = () => {
    setReviewValues(null);
    setUploadState("idle");
    setImagePreview(null);
    setSelectedFile(null);
  };

  const getSummaryText = (fc) => {
    if (fc <= 1) return "Values appear within commonly cited ranges.";
    if (fc <= 3) return "Mixed pattern \u2014 worth discussing with your doctor.";
    return "Several markers outside common ranges. Clinical review recommended.";
  };

  // FEATURE 2: Get severity badge colors
  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'none': return { bg: '#D1FAE5', color: '#065F46' };
      case 'low': return { bg: '#FEF9C3', color: '#854D0E' };
      case 'moderate': return { bg: '#FED7AA', color: '#92400E' };
      case 'high': return { bg: '#FDE8E8', color: '#9B1C1C' };
      default: return { bg: '#EEEAF8', color: '#6D5EF5' };
    }
  };

  // FEATURE 3: Format date for history display
  const formatHistoryDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (showResults && results) {
    // FEATURE 3: Get recent history for trend chart
    const recentHistory = hormoneHistory.slice(-6);
    const maxRatio = recentHistory.length > 0 ? Math.max(...recentHistory.map(h => parseFloat(h.lhFshRatio || 0))) : 1;
    const firstRatio = recentHistory.length > 0 ? parseFloat(recentHistory[0].lhFshRatio || 0) : 0;
    const lastRatio = recentHistory.length > 0 ? parseFloat(recentHistory[recentHistory.length - 1].lhFshRatio || 0) : 0;
    const trendingUp = lastRatio > firstRatio;

    // Generate dynamic overview text based on pattern
    const getOverviewText = () => {
      if (!hormonePattern) return null;
      
      const lines = [];
      
      if (results.flagCount === 0) {
        lines.push("All hormone markers are within normal ranges.");
        lines.push("Continue monitoring with regular check-ups.");
      } else if (results.flagCount <= 2) {
        lines.push("Some hormone markers appear slightly outside typical ranges.");
        lines.push("This may be normal variation or worth discussing at your next visit.");
      } else {
        lines.push("Several hormone markers appear elevated.");
        
        if (hormonePattern.pattern?.includes('Insulin')) {
          lines.push("The pattern may suggest insulin resistance.");
        } else if (hormonePattern.pattern?.includes('Classic')) {
          lines.push("The pattern is consistent with classic PCOS hormonal presentation.");
        }
        
        lines.push("Consider discussing these results with a healthcare professional.");
      }
      
      return lines;
    };

    const overviewLines = getOverviewText();

    return (
      <div className="hormones-screen fade-in" data-testid="hormone-analysis">
        <button className="analysis-back" onClick={handleReenter} data-testid="reenter-btn">← Re-enter Values</button>
        <div className="analysis-summary" data-testid="analysis-summary">
          <h2>Your Hormone Pattern</h2>
          <p>{getSummaryText(results.flagCount)}</p>
        </div>

        {/* Hormone Overview Summary Card */}
        {overviewLines && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(167,139,250,0.1), rgba(109,94,245,0.1))',
            borderRadius: 16, padding: '16px 20px', marginBottom: 20,
            border: '1px solid rgba(109,94,245,0.2)'
          }} data-testid="hormone-overview-card">
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#6D5EF5', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Hormone Overview
            </h4>
            {overviewLines.map((line, idx) => (
              <p key={idx} style={{ 
                fontSize: 13, color: '#2F2F35', lineHeight: 1.5, 
                marginBottom: idx < overviewLines.length - 1 ? 8 : 0,
                margin: idx < overviewLines.length - 1 ? '0 0 8px 0' : 0
              }}>
                {line}
              </p>
            ))}
          </div>
        )}

        {/* FEATURE 2: Detected Pattern Card */}
        {hormonePattern && (
          <div className="pattern-card" data-testid="pattern-card" style={{
            background: '#fff', borderRadius: 20, boxShadow: '0 4px 24px rgba(109,94,245,0.09)',
            padding: '20px 24px', marginBottom: 20
          }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#8A8A9A', letterSpacing: '0.08em', marginBottom: 8 }}>
              DETECTED PATTERN
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: '#2F2F35' }}>{hormonePattern.pattern}</span>
              <span style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: getSeverityStyle(hormonePattern.severity).bg,
                color: getSeverityStyle(hormonePattern.severity).color
              }}>
                {hormonePattern.severity === 'none' ? 'Normal' : hormonePattern.severity.charAt(0).toUpperCase() + hormonePattern.severity.slice(1)}
              </span>
            </div>
            
            {/* Pattern Explanation */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 12,
              borderLeft: '3px solid #A78BFA'
            }}>
              <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>
                {hormonePattern.pattern === 'Classic PCOS Hormonal Pattern' && (
                  <>This pattern shows elevated LH relative to FSH along with higher androgen levels. It is commonly seen in polycystic ovary syndrome and may affect ovulation regularity.</>
                )}
                {hormonePattern.pattern === 'Insulin-Resistant Pattern' && (
                  <>This pattern may suggest reduced insulin sensitivity, which is commonly associated with PCOS. Lifestyle modifications like balanced nutrition and regular exercise can help support metabolic health.</>
                )}
                {hormonePattern.pattern === 'Mild Hormonal Variation' && (
                  <>One hormone marker appears slightly outside typical ranges. This may be normal variation or could warrant monitoring over time. Many factors can influence hormone levels.</>
                )}
                {hormonePattern.pattern === 'Within Common Reference Ranges' && (
                  <>All tested hormones fall within commonly cited reference ranges. Continue monitoring periodically as hormone levels can fluctuate with your cycle and other factors.</>
                )}
                {hormonePattern.pattern === 'Mixed Hormonal Pattern' && (
                  <>Multiple hormone markers show variation from typical ranges. This mixed pattern may benefit from a comprehensive evaluation with a healthcare provider to understand contributing factors.</>
                )}
              </p>
            </div>
            
            {/* What this means section */}
            {hormonePattern.flags && hormonePattern.flags.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 8 }}>
                  Key Findings:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {hormonePattern.flags.includes('elevated_lh_fsh') && (
                    <span style={{ background: '#FEF3C7', color: '#92400E', padding: '4px 10px', borderRadius: 8, fontSize: 11 }}>
                      LH/FSH ratio elevated
                    </span>
                  )}
                  {hormonePattern.flags.includes('elevated_testosterone') && (
                    <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '4px 10px', borderRadius: 8, fontSize: 11 }}>
                      Testosterone elevated
                    </span>
                  )}
                  {hormonePattern.flags.includes('elevated_insulin') && (
                    <span style={{ background: '#FEF3C7', color: '#92400E', padding: '4px 10px', borderRadius: 8, fontSize: 11 }}>
                      Insulin elevated
                    </span>
                  )}
                  {hormonePattern.flags.includes('elevated_amh') && (
                    <span style={{ background: '#E0E7FF', color: '#3730A3', padding: '4px 10px', borderRadius: 8, fontSize: 11 }}>
                      AMH elevated
                    </span>
                  )}
                </div>
              </div>
            )}
            
            <p style={{ fontSize: 11, fontStyle: 'italic', color: '#9CA3AF', margin: 0 }}>
              This is educational information only and does not constitute a medical diagnosis. Please consult a healthcare provider for interpretation.
            </p>
          </div>
        )}

        <div className="bar-chart-card" data-testid="bar-chart">
          {hormoneConfig.map((h, index) => {
            const value = results[h.key]?.value || 0;
            const status = results[h.key]?.status.type || "normal";
            const markerPosition = Math.min((value / h.maxScale) * 100, 100);
            const normalZoneStart = (h.lowRef / h.maxScale) * 100;
            const normalZoneEnd = (h.highRef / h.maxScale) * 100;
            
            // Determine marker color
            let markerColor = '#10B981'; // green for normal
            if (status === 'elevated') markerColor = '#EF4444'; // red
            else if (status === 'borderline') markerColor = '#F59E0B'; // orange
            else if (status === 'low') markerColor = '#3B82F6'; // blue for low
            
            return (
              <div key={h.key} className={`fade-in stagger-${index + 1}`} style={{ marginBottom: 20 }} data-testid={`bar-${h.key}`}>
                {/* Label and Value Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#2F2F35' }}>{h.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#2F2F35' }}>{value} {h.unit}</span>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 600,
                      background: status === 'elevated' ? '#FEE2E2' : status === 'borderline' ? '#FEF3C7' : status === 'low' ? '#DBEAFE' : '#D1FAE5',
                      color: status === 'elevated' ? '#991B1B' : status === 'borderline' ? '#92400E' : status === 'low' ? '#1E40AF' : '#065F46'
                    }}>
                      {results[h.key]?.status.label || "N/A"}
                    </span>
                  </div>
                </div>
                
                {/* Enhanced Bar Visualization */}
                <div style={{ position: 'relative', height: 32, marginBottom: 4 }}>
                  {/* Background track with zones */}
                  <div style={{
                    position: 'absolute',
                    top: 8,
                    left: 0,
                    right: 0,
                    height: 16,
                    borderRadius: 8,
                    background: '#F3F4F6',
                    overflow: 'hidden'
                  }}>
                    {/* Low zone (left) */}
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: `${normalZoneStart}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #DBEAFE, #E0F2FE)'
                    }} />
                    {/* Normal zone (middle) - green */}
                    <div style={{
                      position: 'absolute',
                      left: `${normalZoneStart}%`,
                      top: 0,
                      width: `${normalZoneEnd - normalZoneStart}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #D1FAE5, #A7F3D0)'
                    }} />
                    {/* High zone (right) */}
                    <div style={{
                      position: 'absolute',
                      left: `${normalZoneEnd}%`,
                      top: 0,
                      width: `${100 - normalZoneEnd}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #FEF3C7, #FEE2E2)'
                    }} />
                  </div>
                  
                  {/* Value marker */}
                  <div style={{
                    position: 'absolute',
                    left: mounted ? `calc(${markerPosition}% - 12px)` : '-12px',
                    top: 0,
                    width: 24,
                    height: 32,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    transition: 'left 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}>
                    {/* Triangle pointer */}
                    <div style={{
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: `8px solid ${markerColor}`
                    }} />
                    {/* Vertical line */}
                    <div style={{
                      width: 3,
                      height: 18,
                      background: markerColor,
                      borderRadius: 2
                    }} />
                  </div>
                </div>
                
                {/* Reference labels */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9CA3AF' }}>
                  <span>0</span>
                  <span style={{ position: 'relative', left: `${normalZoneStart - 5}%` }}>{h.lowRef}</span>
                  <span style={{ position: 'relative', left: `${normalZoneEnd - 50}%` }}>{h.highRef}</span>
                  <span>{h.maxScale}</span>
                </div>
              </div>
            );
          })}
        </div>
        {hormoneConfig.map((h) => (
          <div key={h.key} className="hormone-detail-card" data-testid={`detail-${h.key}`}>
            <div className="hormone-detail-top">
              <h4>{h.label}</h4>
              <span className={`badge badge-${results[h.key]?.status.type || "normal"}`}>{results[h.key]?.status.label || "N/A"}</span>
            </div>
            
            {/* Value and Reference Range */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: 12, 
              marginTop: 12,
              marginBottom: 12 
            }}>
              <div style={{ 
                background: '#F7F6FB', 
                borderRadius: 10, 
                padding: '10px 12px' 
              }}>
                <div style={{ fontSize: 11, color: '#8A8A9A', marginBottom: 2 }}>Your Value</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35' }}>
                  {results[h.key]?.value || '—'} <span style={{ fontSize: 12, fontWeight: 500, color: '#8A8A9A' }}>{h.unit}</span>
                </div>
              </div>
              <div style={{ 
                background: '#F7F6FB', 
                borderRadius: 10, 
                padding: '10px 12px' 
              }}>
                <div style={{ fontSize: 11, color: '#8A8A9A', marginBottom: 2 }}>Reference Range</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35' }}>
                  {h.refRange} <span style={{ fontSize: 12, fontWeight: 500, color: '#8A8A9A' }}>{h.unit}</span>
                </div>
              </div>
            </div>

            {/* Expanded Explanation */}
            {(() => {
              const statusType = results[h.key]?.status.type || 'normal';
              const interp = interpretations[h.key]?.[statusType];
              const bgColor = statusType === 'elevated' ? '#FEF3C7' : 
                             statusType === 'borderline' ? '#FEF3C7' :
                             statusType === 'low' ? '#DBEAFE' : '#D1FAE5';
              const headerColor = statusType === 'elevated' ? '#92400E' : 
                                 statusType === 'borderline' ? '#92400E' :
                                 statusType === 'low' ? '#1E40AF' : '#065F46';
              
              return interp && typeof interp === 'object' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* What this means */}
                  <div style={{ 
                    background: bgColor,
                    borderRadius: 10,
                    padding: '12px 14px'
                  }}>
                    <div style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      color: headerColor,
                      marginBottom: 6,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <span>📊</span> What This Means
                    </div>
                    <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>
                      {interp.meaning}
                    </p>
                  </div>
                  
                  {/* Why it matters in PCOS */}
                  <div style={{ 
                    background: '#F3F4F6',
                    borderRadius: 10,
                    padding: '12px 14px',
                    borderLeft: '3px solid #A78BFA'
                  }}>
                    <div style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      color: '#6D5EF5',
                      marginBottom: 6,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <span>💜</span> Why It Matters in PCOS
                    </div>
                    <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>
                      {interp.pcos}
                    </p>
                  </div>
                  
                  {/* Lifestyle considerations */}
                  <div style={{ 
                    background: '#F0FDF4',
                    borderRadius: 10,
                    padding: '12px 14px',
                    borderLeft: '3px solid #10B981'
                  }}>
                    <div style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      color: '#059669',
                      marginBottom: 6,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <span>🌿</span> Lifestyle Considerations
                    </div>
                    <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>
                      {interp.lifestyle}
                    </p>
                  </div>
                </div>
              ) : (
                <div style={{ 
                  background: bgColor,
                  borderRadius: 10,
                  padding: '12px 14px'
                }}>
                  <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, margin: 0 }}>
                    {results[h.key]?.interpretation || ""}
                  </p>
                </div>
              );
            })()}
          </div>
        ))}

        {/* FEATURE 3: Past Reports Section */}
        {hormoneHistory.length > 0 && (
          <div style={{ marginTop: 24 }} data-testid="past-reports">
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Past Reports</h3>
            
            {/* Trend mini-chart - only show if 3+ entries */}
            {hormoneHistory.length >= 3 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>LH/FSH Ratio Trend</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', height: 72, gap: 8 }}>
                  {recentHistory.map((entry, idx) => {
                    const ratio = parseFloat(entry.lhFshRatio || 0);
                    const barHeight = Math.max((ratio / maxRatio) * 60, 6);
                    return (
                      <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{
                          width: '100%', height: barHeight, borderRadius: '6px 6px 0 0',
                          background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)'
                        }} />
                        <span style={{ fontSize: 10, color: '#8A8A9A', marginTop: 4 }}>
                          {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Trend status */}
                <div style={{
                  background: trendingUp ? '#FEF3C7' : '#D1FAE5',
                  borderRadius: 10, padding: '10px 14px', fontSize: 13, marginTop: 8
                }}>
                  {trendingUp 
                    ? '⚠ LH/FSH ratio trending upward over recent reports.'
                    : '✓ LH/FSH ratio stable or improving.'}
                </div>
              </div>
            )}

            {/* History entries */}
            {hormoneHistory.slice().reverse().slice(0, 5).map((entry, idx) => {
              // Determine status for each hormone
              const getStatus = (key, value) => {
                const v = parseFloat(value) || 0;
                if (key === 'lh') return v > 10 ? 'Elevated' : v < 2 ? 'Low' : 'Normal';
                if (key === 'fsh') return v > 10 ? 'Elevated' : v < 3 ? 'Low' : 'Normal';
                if (key === 'insulin') return v > 12 ? 'Elevated' : v > 8 ? 'Borderline' : 'Normal';
                if (key === 'testosterone') return v > 70 ? 'Elevated' : v < 15 ? 'Low' : 'Normal';
                return 'Normal';
              };
              
              const getStatusColor = (status) => {
                if (status === 'Elevated') return { bg: '#FEE2E2', color: '#991B1B' };
                if (status === 'Borderline') return { bg: '#FEF3C7', color: '#92400E' };
                if (status === 'Low') return { bg: '#DBEAFE', color: '#1E40AF' };
                return { bg: '#D1FAE5', color: '#065F46' };
              };
              
              const insulinStatus = getStatus('insulin', entry.insulin);
              
              return (
                <div key={idx} style={{
                  background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(109,94,245,0.09)',
                  padding: '16px 18px', marginBottom: 12,
                  border: '1px solid #F3F4F6'
                }}>
                  {/* Header Row: Date and Pattern */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#2F2F35' }}>
                        {formatHistoryDate(entry.date)}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                        Report #{hormoneHistory.length - idx}
                      </div>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)',
                      padding: '6px 12px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#6D5EF5'
                    }}>
                      {entry.pattern?.replace(' Pattern', '').replace('Within Common Reference Ranges', 'Normal') || 'N/A'}
                    </div>
                  </div>
                  
                  {/* Hormone Values Grid */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: 8,
                    marginBottom: 10
                  }}>
                    <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, color: '#8A8A9A', marginBottom: 2 }}>LH</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#2F2F35' }}>{entry.lh} <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF' }}>mIU/mL</span></div>
                    </div>
                    <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, color: '#8A8A9A', marginBottom: 2 }}>FSH</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#2F2F35' }}>{entry.fsh} <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF' }}>mIU/mL</span></div>
                    </div>
                    <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, color: '#8A8A9A', marginBottom: 2 }}>Fasting Insulin</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#2F2F35' }}>{entry.insulin || '—'}</span>
                        {entry.insulin && (
                          <span style={{
                            fontSize: 9,
                            padding: '2px 6px',
                            borderRadius: 6,
                            background: getStatusColor(insulinStatus).bg,
                            color: getStatusColor(insulinStatus).color,
                            fontWeight: 600
                          }}>
                            {insulinStatus}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, color: '#8A8A9A', marginBottom: 2 }}>Testosterone</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#2F2F35' }}>{entry.testosterone || '—'} <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF' }}>ng/dL</span></div>
                    </div>
                  </div>
                  
                  {/* LH/FSH Ratio */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: parseFloat(entry.lhFshRatio) > 2 ? '#FEF3C7' : '#D1FAE5',
                    borderRadius: 8
                  }}>
                    <span style={{ fontSize: 12, color: parseFloat(entry.lhFshRatio) > 2 ? '#92400E' : '#065F46' }}>
                      LH/FSH Ratio
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: parseFloat(entry.lhFshRatio) > 2 ? '#92400E' : '#065F46' }}>
                      {entry.lhFshRatio} {parseFloat(entry.lhFshRatio) > 2 ? '(Elevated)' : '(Normal)'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="analysis-footer">Reference ranges vary by laboratory. This is not a medical diagnosis.</p>
      </div>
    );
  }

  if (uploadState === "reading") {
    return (
      <div className="hormones-screen" data-testid="upload-loading">
        <div className="loading-state">
          <div className="sphere sphere-md" />
          <p className="loading-text">Reading your report…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hormones-screen fade-in" data-testid="hormones-screen">
      <h2>Hormones</h2>
      
      {/* Enhanced Hormonal Overview - Summary Card with Key Values */}
      {hormoneAssessment && (
        <div style={{
          background: 'linear-gradient(135deg, #FAFAFF 0%, #F5F3FF 50%, #EDE9FE 100%)',
          borderRadius: 16,
          padding: '18px 18px 14px',
          marginBottom: 20,
          border: '1px solid rgba(167, 139, 250, 0.25)',
          boxShadow: '0 2px 12px rgba(109,94,245,0.08)'
        }} data-testid="hormonal-overview-card">
          {/* Header with Overall Status */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: 14 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18
              }}>
                🧬
              </div>
              <div>
                <h3 style={{ 
                  fontSize: 15, 
                  fontWeight: 700, 
                  color: '#2F2F35', 
                  margin: 0,
                  lineHeight: 1.2
                }}>
                  Your Hormonal Overview
                </h3>
                <p style={{ fontSize: 11, color: '#8A8A9A', margin: '2px 0 0 0' }}>
                  Based on your latest report
                </p>
              </div>
            </div>
            {/* Overall Status Badge */}
            <div style={{
              padding: '6px 14px',
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 700,
              background: hormoneAssessment.color.bg,
              color: hormoneAssessment.color.text,
              border: `1px solid ${hormoneAssessment.color.badge}20`
            }}>
              {hormoneAssessment.overall}
            </div>
          </div>
          
          {/* Key Hormone Values Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
            marginBottom: 14
          }}>
            {/* LH */}
            <div style={{
              background: '#fff',
              borderRadius: 10,
              padding: '10px 12px',
              border: '1px solid #F0EEF8'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 500 }}>LH</span>
                <span style={{
                  fontSize: 9,
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 600,
                  background: hormoneAssessment.statuses.lh === 'elevated' ? '#FEE2E2' : 
                             hormoneAssessment.statuses.lh === 'low' ? '#DBEAFE' : '#D1FAE5',
                  color: hormoneAssessment.statuses.lh === 'elevated' ? '#991B1B' : 
                         hormoneAssessment.statuses.lh === 'low' ? '#1E40AF' : '#065F46'
                }}>
                  {hormoneAssessment.statuses.lh === 'elevated' ? 'HIGH' : 
                   hormoneAssessment.statuses.lh === 'low' ? 'LOW' : 'OK'}
                </span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', marginTop: 4 }}>
                {hormoneAssessment.values.lh} <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF' }}>mIU/mL</span>
              </div>
            </div>
            
            {/* FSH */}
            <div style={{
              background: '#fff',
              borderRadius: 10,
              padding: '10px 12px',
              border: '1px solid #F0EEF8'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 500 }}>FSH</span>
                <span style={{
                  fontSize: 9,
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 600,
                  background: hormoneAssessment.statuses.fsh === 'elevated' ? '#FEE2E2' : 
                             hormoneAssessment.statuses.fsh === 'low' ? '#DBEAFE' : '#D1FAE5',
                  color: hormoneAssessment.statuses.fsh === 'elevated' ? '#991B1B' : 
                         hormoneAssessment.statuses.fsh === 'low' ? '#1E40AF' : '#065F46'
                }}>
                  {hormoneAssessment.statuses.fsh === 'elevated' ? 'HIGH' : 
                   hormoneAssessment.statuses.fsh === 'low' ? 'LOW' : 'OK'}
                </span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', marginTop: 4 }}>
                {hormoneAssessment.values.fsh} <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF' }}>mIU/mL</span>
              </div>
            </div>
            
            {/* Testosterone */}
            <div style={{
              background: '#fff',
              borderRadius: 10,
              padding: '10px 12px',
              border: '1px solid #F0EEF8'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 500 }}>Testosterone</span>
                <span style={{
                  fontSize: 9,
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 600,
                  background: hormoneAssessment.statuses.testosterone === 'elevated' ? '#FEE2E2' : 
                             hormoneAssessment.statuses.testosterone === 'low' ? '#DBEAFE' : '#D1FAE5',
                  color: hormoneAssessment.statuses.testosterone === 'elevated' ? '#991B1B' : 
                         hormoneAssessment.statuses.testosterone === 'low' ? '#1E40AF' : '#065F46'
                }}>
                  {hormoneAssessment.statuses.testosterone === 'elevated' ? 'HIGH' : 
                   hormoneAssessment.statuses.testosterone === 'low' ? 'LOW' : 'OK'}
                </span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', marginTop: 4 }}>
                {hormoneAssessment.values.testosterone} <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF' }}>ng/dL</span>
              </div>
            </div>
            
            {/* Insulin */}
            <div style={{
              background: '#fff',
              borderRadius: 10,
              padding: '10px 12px',
              border: '1px solid #F0EEF8'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 500 }}>Fasting Insulin</span>
                <span style={{
                  fontSize: 9,
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 600,
                  background: hormoneAssessment.statuses.insulin === 'elevated' ? '#FEE2E2' : 
                             hormoneAssessment.statuses.insulin === 'borderline' ? '#FEF3C7' : '#D1FAE5',
                  color: hormoneAssessment.statuses.insulin === 'elevated' ? '#991B1B' : 
                         hormoneAssessment.statuses.insulin === 'borderline' ? '#92400E' : '#065F46'
                }}>
                  {hormoneAssessment.statuses.insulin === 'elevated' ? 'HIGH' : 
                   hormoneAssessment.statuses.insulin === 'borderline' ? 'WATCH' : 'OK'}
                </span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', marginTop: 4 }}>
                {hormoneAssessment.values.insulin} <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF' }}>µIU/mL</span>
              </div>
            </div>
          </div>
          
          {/* LH/FSH Ratio - Key PCOS Indicator */}
          <div style={{
            background: hormoneAssessment.statuses.lhFshRatio === 'elevated' ? '#FEF3C7' : 
                       hormoneAssessment.statuses.lhFshRatio === 'borderline' ? '#FEF9C3' : '#D1FAE5',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ 
              fontSize: 12, 
              fontWeight: 500,
              color: hormoneAssessment.statuses.lhFshRatio === 'elevated' ? '#92400E' : 
                     hormoneAssessment.statuses.lhFshRatio === 'borderline' ? '#854D0E' : '#065F46'
            }}>
              LH/FSH Ratio
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ 
                fontSize: 15, 
                fontWeight: 700,
                color: hormoneAssessment.statuses.lhFshRatio === 'elevated' ? '#92400E' : 
                       hormoneAssessment.statuses.lhFshRatio === 'borderline' ? '#854D0E' : '#065F46'
              }}>
                {hormoneAssessment.values.lhFshRatio}
              </span>
              <span style={{
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 10,
                fontWeight: 600,
                background: 'rgba(255,255,255,0.6)',
                color: hormoneAssessment.statuses.lhFshRatio === 'elevated' ? '#B45309' : 
                       hormoneAssessment.statuses.lhFshRatio === 'borderline' ? '#A16207' : '#047857'
              }}>
                {hormoneAssessment.statuses.lhFshRatio === 'elevated' ? 'Elevated (>2)' : 
                 hormoneAssessment.statuses.lhFshRatio === 'borderline' ? 'Borderline' : 'Normal (<1.5)'}
              </span>
            </div>
          </div>
          
          {/* Interpretation */}
          <p style={{ 
            fontSize: 13, 
            color: '#374151', 
            lineHeight: 1.5, 
            margin: 0,
            padding: '8px 0 0 0',
            borderTop: '1px solid rgba(167, 139, 250, 0.15)'
          }}>
            {hormoneAssessment.interpretation}
          </p>
        </div>
      )}
      
      {/* Empty State - Show only when NO hormone data exists at all */}
      {!showResults && !hormoneAssessment && subTab === "upload" && !imagePreview && !selectedFile && uploadState !== "review" && (
        <div style={{
          background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)',
          borderRadius: 20,
          padding: 24,
          textAlign: 'center',
          marginBottom: 20
        }} data-testid="hormone-empty-state">
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#2F2F35', marginBottom: 8 }}>
            No Hormone Report Yet
          </h3>
          <p style={{ fontSize: 14, color: '#5A5A5A', marginBottom: 20, lineHeight: 1.6 }}>
            Upload your first blood report to unlock personalized hormone insights and track your patterns over time.
          </p>
          <button 
            className="btn-primary" 
            onClick={() => fileInputRef.current?.click()}
            style={{ padding: '14px 32px' }}
            data-testid="upload-first-report-btn"
          >
            Upload Report
          </button>
          <p style={{ fontSize: 12, color: '#8A8A9A', marginTop: 12 }}>
            or <button 
              onClick={() => setSubTab("manual")} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#6D5EF5', 
                fontWeight: 600, 
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              enter values manually
            </button>
          </p>
        </div>
      )}

      <div className="hormones-subtabs">
        <button className={`hormones-subtab${subTab === "upload" ? " active" : ""}`} onClick={() => setSubTab("upload")} data-testid="subtab-upload">Upload Report</button>
        <button className={`hormones-subtab${subTab === "manual" ? " active" : ""}`} onClick={() => setSubTab("manual")} data-testid="subtab-manual">Enter Manually</button>
      </div>

      {subTab === "upload" && uploadState === "review" && reviewValues && (
        <div
          style={{
            background: "#fff",
            borderRadius: 20,
            padding: 20,
            marginBottom: 16,
            boxShadow: "0 4px 24px rgba(109,94,245,0.09)",
            border: "2px solid #A78BFA",
          }}
          data-testid="review-screen"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: "linear-gradient(135deg, #A78BFA, #6D5EF5)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#2F2F35", margin: 0 }}>
                Review Extracted Values
              </h3>
              <p style={{ fontSize: 12, color: "#8A8A9A", margin: 0 }}>
                Verify and edit if needed before confirming
              </p>
            </div>
          </div>

          {reviewValues.summary && (
            <div style={{
              background: "#F3F4F6", borderRadius: 10, padding: "10px 12px",
              fontSize: 12, color: "#374151", marginBottom: 14, lineHeight: 1.5,
            }}>
              {reviewValues.summary}
            </div>
          )}

          {/* Side-by-side preview: original report + extracted values */}
          {imagePreview && (
            <div style={{ marginBottom: 14 }} data-testid="review-preview-image">
              <div style={{ fontSize: 11, color: "#8A8A9A", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                Your uploaded report (compare with values below)
              </div>
              <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #EEEAF8", background: "#F7F6FB" }}>
                <img
                  src={imagePreview}
                  alt="Uploaded report"
                  style={{ width: "100%", maxHeight: 220, objectFit: "contain", display: "block" }}
                />
              </div>
            </div>
          )}
          {!imagePreview && selectedFile?.type === "application/pdf" && (
            <div style={{
              marginBottom: 14, padding: 12, background: "#F7F6FB",
              borderRadius: 10, border: "1px dashed #EEEAF8", display: "flex",
              alignItems: "center", gap: 10,
            }} data-testid="review-preview-pdf">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>PDF report</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#2F2F35", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedFile.name}
                </div>
                <div style={{ fontSize: 11, color: "#8A8A9A", marginTop: 2 }}>
                  Extracted from PDF — verify values below
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { key: "lh", label: "LH (Luteinizing Hormone)", unit: "mIU/mL" },
              { key: "fsh", label: "FSH (Follicle Stimulating)", unit: "mIU/mL" },
              { key: "insulin", label: "Fasting Insulin", unit: "uIU/mL" },
              { key: "testosterone", label: "Total Testosterone", unit: "ng/dL" },
              { key: "amh", label: "AMH (Anti-Mullerian)", unit: "ng/mL" },
            ].map((h) => (
              <div key={h.key} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 12px", background: "#F9FAFB", borderRadius: 10,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 2 }}>{h.label}</div>
                  <input
                    type="number"
                    step="0.01"
                    value={reviewValues[h.key]}
                    onChange={(e) => updateReviewField(h.key, e.target.value)}
                    placeholder="Not detected"
                    style={{
                      width: "100%", border: "1px solid #EEEAF8",
                      borderRadius: 6, padding: "6px 8px", fontSize: 14, fontWeight: 600,
                      color: reviewValues[h.key] ? "#6D5EF5" : "#9CA3AF",
                      background: "#fff",
                    }}
                    data-testid={`review-input-${h.key}`}
                  />
                </div>
                <span style={{ fontSize: 11, color: "#8A8A9A", whiteSpace: "nowrap" }}>{h.unit}</span>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button
              onClick={handleEditReview}
              style={{
                flex: 1, padding: 12, background: "#fff",
                color: "#6D5EF5", border: "2px solid #6D5EF5", borderRadius: 12,
                fontWeight: 600, fontSize: 14, cursor: "pointer",
              }}
              data-testid="review-edit-btn"
            >
              Edit in Form
            </button>
            <button
              onClick={handleConfirmReview}
              className="btn-primary"
              style={{ flex: 1 }}
              data-testid="review-confirm-btn"
            >
              Confirm &amp; Analyze
            </button>
          </div>
          <button
            onClick={handleCancelReview}
            style={{
              marginTop: 10, width: "100%", padding: 8,
              background: "transparent", border: "none",
              color: "#8A8A9A", fontSize: 13, cursor: "pointer",
            }}
            data-testid="review-cancel-btn"
          >
            Cancel and start over
          </button>
        </div>
      )}

      {subTab === "upload" && uploadState !== "review" && (
        <div className="upload-zone" data-testid="upload-zone">
          {uploadError && (
            <div style={{
              background: '#FEE2E2', color: '#991B1B', padding: '10px 14px',
              borderRadius: 10, fontSize: 13, marginBottom: 12, border: '1px solid #FCA5A5'
            }} data-testid="upload-error">
              {uploadError}
            </div>
          )}
          {!selectedFile ? (
            <>
              <div className="sphere sphere-sm" style={{ margin: "0 auto 14px" }} />
              <h3>Upload your blood test report</h3>
              <p>JPG, PNG or PDF (max 10 MB)</p>
              <button className="btn-choose" onClick={() => fileInputRef.current?.click()} data-testid="choose-file-btn">Choose File</button>
            </>
          ) : (
            <>
              {/* Image Preview or PDF placeholder */}
              {imagePreview ? (
                <div style={{ marginBottom: 16, borderRadius: 12, overflow: 'hidden', border: '2px solid #EEEAF8' }}>
                  <img
                    src={imagePreview}
                    alt="Report preview"
                    style={{ width: '100%', maxHeight: 200, objectFit: 'contain', background: '#F7F6FB' }}
                  />
                </div>
              ) : (
                <div style={{
                  marginBottom: 16, padding: 24, borderRadius: 12, border: '2px dashed #EEEAF8',
                  background: '#F7F6FB', textAlign: 'center'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2" style={{ margin: '0 auto 8px', display: 'block' }}>
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{selectedFile.name}</div>
                  <div style={{ fontSize: 11, color: '#8A8A9A', marginTop: 4 }}>
                    PDF — text will be extracted from all pages (up to 3)
                  </div>
                </div>
              )}
              <p style={{ fontSize: 13, color: '#065F46', marginBottom: 12 }} data-testid="file-selected-msg">
                {uploadState === "uploading" && "Uploading…"}
                {uploadState === "extracting" && "Reading hormone values…"}
                {uploadState === "analyzing" && "Analyzing…"}
                {(uploadState === "idle" || uploadState === "fallback") && "File ready to upload"}
              </p>
              <button
                className="btn-primary"
                onClick={handleUploadAndEdit}
                disabled={uploadState === "uploading" || uploadState === "extracting" || uploadState === "analyzing"}
                data-testid="analyze-upload-btn"
              >
                {uploadState === "uploading" && "Uploading…"}
                {uploadState === "extracting" && "Extracting hormones…"}
                {uploadState === "analyzing" && "Analyzing…"}
                {(uploadState === "idle" || uploadState === "fallback") && "Upload & Analyze"}
              </button>
              <button
                className="btn-ghost"
                onClick={() => { setSelectedFile(null); setImagePreview(null); setUploadError(""); }}
                style={{ marginTop: 8, fontSize: 13 }}
                data-testid="choose-different-btn"
              >
                Choose Different File
              </button>
            </>
          )}
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileSelect(e.target.files[0])}
            accept=".jpg,.jpeg,.png,.pdf"
            style={{ display: "none" }}
            data-testid="file-input"
          />
        </div>
      )}

      {subTab === "manual" && (
        <>
          {uploadState === "fallback" && imagePreview && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '2px solid #EEEAF8', marginBottom: 8 }}>
                <img 
                  src={imagePreview} 
                  alt="Report reference" 
                  style={{ width: '100%', maxHeight: 150, objectFit: 'contain', background: '#F7F6FB' }}
                />
              </div>
              <p style={{ fontSize: 12, color: '#8A8A9A', textAlign: 'center' }}>Reference your report above while entering values</p>
            </div>
          )}
          {uploadState === "fallback" && (
            <div className="amber-notice" data-testid="amber-notice">Please enter your hormone values from the report below.</div>
          )}
          {fetchError && <div className="amber-notice">{fetchError}</div>}
          
          {/* Helper Text */}
          {uploadState !== "fallback" && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ 
                fontSize: 15, 
                fontWeight: 600, 
                color: '#2F2F35', 
                marginBottom: 8 
              }}>
                Enter the hormone values from your blood test report.
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '10px 12px',
                background: '#F0F9FF',
                borderRadius: 10,
                border: '1px solid #BAE6FD'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0284C7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <p style={{ 
                  fontSize: 12, 
                  color: '#0369A1', 
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  Values are usually listed in your lab report under &quot;Hormone Panel&quot; or &quot;Endocrine Tests&quot;. Look for LH, FSH, Fasting Insulin, Testosterone, and AMH.
                </p>
              </div>
            </div>
          )}
          
          <div className="hormone-form-card" data-testid="hormone-form">
            {hormoneConfig.map((h, idx) => (
              <div key={h.key}>
                <div className="form-group">
                  <label>{h.label}<span>{h.unit}</span></label>
                  <input type="number" step="0.01" placeholder={h.placeholder} value={formData[h.key]} onChange={(e) => setFormData((p) => ({ ...p, [h.key]: e.target.value }))} className="input-field" data-testid={`input-${h.key}`} />
                </div>
                {/* FEATURE 1: LH/FSH Ratio Row - Show after FSH input */}
                {h.key === 'fsh' && lhFshRatio && (
                  <div style={{ marginBottom: 14, padding: '12px 0', borderBottom: '1px solid #F0EEF8' }} data-testid="lh-fsh-ratio-row">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>LH/FSH Ratio</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontWeight: 700, color: '#2F2F35', fontSize: 15 }}>{lhFshRatio}</span>
                        <span style={{
                          padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: getRatioStatus(lhFshRatio).bg,
                          color: getRatioStatus(lhFshRatio).color
                        }}>
                          {getRatioStatus(lhFshRatio).label}
                        </span>
                      </div>
                    </div>
                    <p style={{ fontSize: 11, fontStyle: 'italic', color: '#8A8A9A', marginTop: 6, marginBottom: 0 }}>
                      LH/FSH ratio varies by cycle phase. Clinical correlation recommended.
                    </p>
                  </div>
                )}
              </div>
            ))}
            <button type="button" className="btn-primary" onClick={handleAnalyze} disabled={saving} style={{ marginTop: 8 }} data-testid="analyze-hormones-btn">
              {saving ? "Saving\u2026" : "Analyze My Hormones"}
            </button>
          </div>
        </>
      )}

      {/* Medical Disclaimer */}
      <p style={{ 
        fontSize: 11, color: '#8A8A9A', textAlign: 'center', 
        marginTop: 24, lineHeight: 1.5, fontStyle: 'italic' 
      }}>
        This tool provides educational insights and does not replace professional medical advice.
      </p>
    </div>
  );
}
